import { Ingredient, Kitchenware, Recipe, RecipeDifficulty } from "./Recipe"
import { AccessMode, PartialRecipe } from "~/models/PartialRecipe"
import {
  ApiPartialPublicUser,
  apiPartialPublicUserToPartialPublicUser,
  PartialPublicUser,
} from "~/models/PublicUser"
import { VerticalId } from "~/models/Vertical"
import { ChefclubLocale } from "~/utils/locales"

export interface APICommunityStep {
  uuid: string
  description: string
  image: string
  image_hd: string
  image_thumbnail: string
  order: number
  ingredients: Ingredient[]
}

export interface APICommunityCategories {
  uuid: string
  name: string
  slug: string
}
export interface Diet {
  uuid: string
  translation: string
  slug: string
}

export interface APIOrigins {
  uuid: string
  slug: string
  translation: {
    uuid: string
    name: string
    language: string
  }
}

export interface ApiCommunityRecipe {
  uuid: string
  created: string
  title: string
  subtitle: string
  description: string
  duration_minutes: number
  difficulty: RecipeDifficulty
  preparation_minutes: number
  cooking_minutes: number
  resting_minutes: number
  number_of_people: number
  web_cover: string | null
  web_cover_thumbnail: string | null
  is_draft: boolean
  is_complete: boolean
  source: string
  status:
    | "created"
    | "submitted"
    | "validated"
    | "reported"
    | "refused"
    | "blocked"
  kitchenwares_v2: Kitchenware[]
  ingredients_v2: Ingredient[]
  suitable_for_diets: Diet[]
  steps_details: APICommunityStep[]
  categories: APICommunityCategories[]
  reason_for_refusal?: string
  region_of_origin: APIOrigins
  validation_errors: string[]
  url_slug?: string
  vertical?: string
  language: string
  access_mode?: string
  posts_count: number
  absolute_url: string
  share_url: string
  views_count: number
  author?: ApiPartialPublicUser
}

export interface ApiCommunityUpdateRecipe {
  title?: string
  subtitle?: string
  description?: string
  language?: ChefclubLocale
  preparation_minutes?: number
  cooking_minutes?: number
  resting_minutes?: number
  number_of_people?: number
  is_draft?: boolean
  source?: string
  ingredients?: Ingredient[]
  suitable_for_diets?: { uuid: string }[]
  status?: "created" | "submitted" | "validated" | "refused" | "blocked"
  region_of_origin?: { uuid: string }
}

export interface CommunityRecipe {
  id: string
  created: string
  title: string
  subtitle: string
  description: string
  durationMinutes: number
  difficulty: RecipeDifficulty
  preparationMinutes: number
  cookingMinutes: number
  restingMinutes: number
  numberOfPeople: number
  webCover: string | null
  webCoverThumbnail: string | null
  isDraft: boolean
  isComplete: boolean
  source: string
  status:
    | "created"
    | "submitted"
    | "validated"
    | "reported"
    | "refused"
    | "blocked"
  kitchenwares: Kitchenware[]
  ingredients: Ingredient[]
  suitableForDiets: Diet[]
  steps: APICommunityStep[]
  validationErrors: string[]
  regionOfOrigin: APIOrigins
  urlSlug?: string
  vertical?: string
  language: string
  accessMode?: string
  postsCount: number
  absoluteUrl: string
  shareUrl: string
  viewsCount: number
  author?: PartialPublicUser
}

export const apiCommunityRecipeToPartialRecipe = (
  apiCommunityRecipe: ApiCommunityRecipe
): PartialRecipe => ({
  id: apiCommunityRecipe.uuid,
  slug: "",
  title: apiCommunityRecipe.title,
  vertical: "daily",
  webCover: apiCommunityRecipe.web_cover ?? "",
  webCoverThumbnail: apiCommunityRecipe.web_cover_thumbnail ?? "",
  accessMode: "public",
  language: "",
})

export const isCommunityRecipe = (arg: any): arg is CommunityRecipe => {
  return "isDraft" in arg && "isComplete" in arg
}

export const apiCommunityRecipeToCommunityRecipe = (
  apiCommunityRecipe: ApiCommunityRecipe
): CommunityRecipe => ({
  id: apiCommunityRecipe.uuid,
  created: apiCommunityRecipe.created,
  title: apiCommunityRecipe.title,
  subtitle: apiCommunityRecipe.subtitle,
  description: apiCommunityRecipe.description,
  durationMinutes: apiCommunityRecipe.duration_minutes,
  difficulty: apiCommunityRecipe.difficulty,
  preparationMinutes: apiCommunityRecipe.preparation_minutes,
  cookingMinutes: apiCommunityRecipe.cooking_minutes,
  restingMinutes: apiCommunityRecipe.resting_minutes,
  numberOfPeople: apiCommunityRecipe.number_of_people,
  webCover: apiCommunityRecipe.web_cover,
  webCoverThumbnail: apiCommunityRecipe.web_cover_thumbnail,
  isDraft: apiCommunityRecipe.is_draft,
  isComplete: apiCommunityRecipe.is_complete,
  source: apiCommunityRecipe.source,
  status: apiCommunityRecipe.status,
  kitchenwares: apiCommunityRecipe.kitchenwares_v2,
  ingredients: apiCommunityRecipe.ingredients_v2,
  suitableForDiets: apiCommunityRecipe.suitable_for_diets,
  steps: apiCommunityRecipe.steps_details,
  validationErrors: apiCommunityRecipe.validation_errors,
  regionOfOrigin: apiCommunityRecipe.region_of_origin,
  urlSlug: apiCommunityRecipe.url_slug,
  vertical: apiCommunityRecipe.vertical,
  language: apiCommunityRecipe.language,
  accessMode: apiCommunityRecipe.access_mode,
  postsCount: apiCommunityRecipe.posts_count,
  absoluteUrl: apiCommunityRecipe.absolute_url,
  shareUrl: apiCommunityRecipe.share_url,
  viewsCount: apiCommunityRecipe.views_count,
  author: apiCommunityRecipe.author
    ? apiPartialPublicUserToPartialPublicUser(apiCommunityRecipe.author)
    : undefined,
})

export const apiCommunityRecipeToRecipe = (
  apiCommunityRecipe: ApiCommunityRecipe
): Recipe => ({
  id: apiCommunityRecipe.uuid,
  slug: apiCommunityRecipe.url_slug ?? "",
  title: apiCommunityRecipe.title,
  vertical: apiCommunityRecipe.vertical as VerticalId,
  webCover: apiCommunityRecipe.web_cover ?? "",
  webCoverThumbnail: apiCommunityRecipe.web_cover_thumbnail ?? "",
  language: apiCommunityRecipe.language ?? "fr",
  accessMode: apiCommunityRecipe.access_mode as AccessMode,
  postsCount: apiCommunityRecipe.posts_count,
  absoluteUrl: apiCommunityRecipe.absolute_url,
  books: [],
  categories: apiCommunityRecipe.categories.map(cat => cat.uuid),
  chefclubVideoId: "",
  createdTime: apiCommunityRecipe.created,
  description: apiCommunityRecipe.description,
  dominantColor: "",
  durationMinutes: apiCommunityRecipe.duration_minutes,
  difficulty: apiCommunityRecipe.difficulty,
  preparationMinutes: apiCommunityRecipe.preparation_minutes,
  cookingMinutes: apiCommunityRecipe.cooking_minutes,
  restingMinutes: apiCommunityRecipe.resting_minutes,
  ingredients: [],
  ingredients_v2: apiCommunityRecipe.ingredients_v2,
  kitchenware: [],
  kitchenware_v2: apiCommunityRecipe.kitchenwares_v2,
  lastModified: new Date(apiCommunityRecipe.created).getDate(),
  marketingDescription: apiCommunityRecipe.description,
  mobileShareUrl: apiCommunityRecipe.share_url,
  numberOfPeople: apiCommunityRecipe.number_of_people,
  // packs,
  pinterestPicture: "",
  pinterestPictureThumbnail: "",
  premiumVideoUrl: "apiCommunityRecipe.premium_video_url",
  primaryCategory: {
    description: null,
    title: null,
    name: null,
    uuid: null,
  },
  primaryTags: [],
  resultPicture: "",
  resultPictureThumbnail: "",
  shareUrl: apiCommunityRecipe.share_url,
  steps: apiCommunityRecipe.steps_details.map(step => step.description),
  stepsDetails: apiCommunityRecipe.steps_details,
  suitableForDiets: apiCommunityRecipe.suitable_for_diets,
  ratingsAverage: null,
  ratingsCount: 0,
  sticker: "",
  stickerPosition: "",
  subtitle: apiCommunityRecipe.subtitle,
  tags: [],
  videoPreviewThumbnail: "",
  videoUrl: "",
  videoWidth: 0,
  videoHeight: 0,
  webCoverHd: apiCommunityRecipe.web_cover || "",
  videoPreview: apiCommunityRecipe.web_cover_thumbnail || "",
  hostedVideoUrl: "",
  exploreRecipes: [],
  trick: "",
  viewsCount: apiCommunityRecipe.views_count,
  author: apiCommunityRecipe.author
    ? apiPartialPublicUserToPartialPublicUser(apiCommunityRecipe.author)
    : null,
  editorialModules: [],
  recipeType: "community",
  otherCategoryRecipes: [],
  sameCategoryRecipes: [],
})
